import { combineLatest, Observable, first, switchMap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Address, InterceptorUtil, OCC_USER_ID_ANONYMOUS, OccEndpointsService, USE_CLIENT_TOKEN, UserIdService } from '@spartacus/core';
import { ActiveCartService } from '@spartacus/cart/base/core';

import { CustomAddress } from '../models';

@Injectable({
  providedIn: 'root'
})
export class CheckoutAddressService {
  constructor(
    private readonly activeCartService: ActiveCartService,
    private readonly userIdService: UserIdService,
    private readonly http: HttpClient,
    private readonly occEndpointsService: OccEndpointsService
  ) {}

  setBillingAddress(address: Address): Observable<boolean> {
    const userIdService$ = this.userIdService.takeUserId(false);
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        const params = { addressId: address?.id };

        const url = this.occEndpointsService.buildUrl('checkoutBillingAddress', {
          urlParams: { userId, cartId },
          queryParams: params
        });

        return this.http.put<boolean>(url, {});
      })
    );
  }

  createAndSetBillingAddress(billingAddress: CustomAddress): Observable<CustomAddress> {
    const userIdService$ = this.userIdService.takeUserId(false);
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        const body = { ...billingAddress.addressGroup, ...billingAddress.contactGroup };

        const url = this.occEndpointsService.buildUrl('checkoutBillingAddress', {
          urlParams: { userId, cartId }
        });

        return this.http.post<CustomAddress>(url, body);
      })
    );
  }

  setDeliveryAddress(address: Address): Observable<boolean> {
    const userIdService$ = this.userIdService.takeUserId(false);
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        const params = { addressId: address?.id };

        const url = this.occEndpointsService.buildUrl('checkoutDeliveryAddress', {
          urlParams: { userId, cartId },
          queryParams: params
        });

        return this.http.put<boolean>(url, {});
      })
    );
  }

  deleteDeliveryAddress(): Observable<boolean> {
    const userIdService$ = this.userIdService.takeUserId(false);
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }

        const url = this.occEndpointsService.buildUrl('checkoutDeliveryAddress', {
          urlParams: { userId, cartId }
        });

        return this.http.delete<boolean>(url, { headers });
      })
    );
  }

  deleteWithdrawalAddress(): Observable<void> {
    const userIdService$ = this.userIdService.takeUserId();
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        const url = this.occEndpointsService.buildUrl('deleteWithdrawalAddress', {
          urlParams: { userId, cartId }
        });
        return this.http.delete<any>(url, {});
      })
    );
  }

  createAndSetDeliveryAddress(address: CustomAddress): Observable<CustomAddress> {
    const userIdService$ = this.userIdService.takeUserId(false);
    const cartService$ = this.activeCartService.getActiveCartId();
    return combineLatest([userIdService$, cartService$]).pipe(
      first(([userId, cartId]) => !!cartId && !!userId),
      switchMap(([userId, cartId]) => {
        const body = { ...address.addressGroup, ...address.contactGroup };
        const url = this.occEndpointsService.buildUrl('checkoutDeliveryAddress', {
          urlParams: { userId, cartId }
        });
        return this.http.post<CustomAddress>(url, body);
      })
    );
  }
}
